import React, { useState, useEffect } from 'react';
import { fetchSettings, updateSettings, downloadBackup, fetchSettingsPresets, PresetItem } from '../api/settings';
import { UserSettings } from '../types/settings';
import { useToast } from '../context/ToastContext';
import {
  Sliders,
  Calendar,
  Download,
  Info,
  Save,
  Zap,
} from 'lucide-react';

export const SettingsPage: React.FC = () => {
  const { showToast } = useToast();

  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [presets, setPresets] = useState<PresetItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);

  // Form string states for array fields
  const [learningStepsStr, setLearningStepsStr] = useState<string>('1, 10');
  const [relearningStepsStr, setRelearningStepsStr] = useState<string>('10');
  const [weightsStr, setWeightsStr] = useState<string>('');

  useEffect(() => {
    Promise.all([
      fetchSettings(),
      fetchSettingsPresets().catch(() => ({ items: [] })),
    ])
      .then(([settingsData, presetsData]) => {
        setSettings(settingsData);
        setPresets(presetsData.items || []);
        setLearningStepsStr((settingsData.learning_steps_minutes || [1, 10]).join(', '));
        setRelearningStepsStr((settingsData.relearning_steps_minutes || [10]).join(', '));
        setWeightsStr(settingsData.weights ? settingsData.weights.join(', ') : '');
      })
      .catch((err: any) => {
        showToast(err.message || '加载设置失败', 'error');
      })
      .finally(() => setLoading(false));
  }, [showToast]);

  const applyPreset = (preset: PresetItem) => {
    if (!settings) return;
    const merged = { ...settings, ...preset.settings };
    setSettings(merged);
    if (merged.learning_steps_minutes) {
      setLearningStepsStr(merged.learning_steps_minutes.join(', '));
    }
    if (merged.relearning_steps_minutes) {
      setRelearningStepsStr(merged.relearning_steps_minutes.join(', '));
    }
    showToast(`已套用「${preset.name}」预设参数，点击保存后生效`, 'info');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;
    setSaving(true);

    try {
      // Parse learning steps
      const learningSteps = learningStepsStr
        .split(/[,，\s]+/)
        .map((s) => parseFloat(s.trim()))
        .filter((n) => !isNaN(n) && n > 0);

      const relearningSteps = relearningStepsStr
        .split(/[,，\s]+/)
        .map((s) => parseFloat(s.trim()))
        .filter((n) => !isNaN(n) && n > 0);

      // Parse weights
      let parsedWeights: number[] | null = null;
      if (weightsStr.trim()) {
        const nums = weightsStr
          .split(/[,，\s]+/)
          .map((s) => parseFloat(s.trim()))
          .filter((n) => !isNaN(n));
        if (nums.length === 21) {
          parsedWeights = nums;
        } else {
          showToast('FSRS 权重参数数组应为恰好 21 个数值，或者留空使用默认', 'warning');
          setSaving(false);
          return;
        }
      }

      const payload: Partial<UserSettings> = {
        desired_retention: settings.desired_retention,
        new_cards_per_day: Number(settings.new_cards_per_day),
        reviews_per_day: Number(settings.reviews_per_day),
        learning_steps_minutes: learningSteps,
        relearning_steps_minutes: relearningSteps,
        maximum_interval_days: Number(settings.maximum_interval_days),
        enable_fuzzing: Boolean(settings.enable_fuzzing),
        weights: parsedWeights,
        timezone: settings.timezone || 'Asia/Shanghai',
        day_start_hour: Number(settings.day_start_hour),
        bury_siblings: Boolean(settings.bury_siblings),
        leech_threshold: Number(settings.leech_threshold),
      };

      const updated = await updateSettings(payload);
      setSettings(updated);
      showToast('设置与算法调度参数已成功保存！', 'success');
    } catch (err: any) {
      showToast(err.message || '保存设置失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleExportBackup = async () => {
    setExporting(true);
    try {
      await downloadBackup();
      showToast('全量数据备份文件已生成并下载', 'success');
    } catch (err: any) {
      showToast(err.message || '导出备份失败', 'error');
    } finally {
      setExporting(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-slate-500">
        <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-xs">加载系统与调度算法配置...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight">FSRS 算法配置与系统设置</h2>
        <p className="text-xs text-slate-500 mt-1">
          Lexi 采用现代 FSRS (Free Spaced Repetition Scheduler) 认知科学记忆模型，拒绝伪造参数，杜绝陈旧 SM-2 算力缺陷
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Presets Row */}
        {presets.length > 0 && (
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              快速调度预设 (Presets)
            </h3>
            <p className="text-xs text-slate-500">
              根据你的学习阶段与每日精力投入，一键选择经科学权衡的参数配置：
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              {presets.map((preset) => (
                <div
                  key={preset.id}
                  onClick={() => applyPreset(preset)}
                  className="p-3.5 rounded-xl border border-slate-200 hover:border-emerald-400 bg-slate-50/60 hover:bg-emerald-50/40 cursor-pointer transition-all group shadow-sm"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-xs text-slate-800 group-hover:text-emerald-700">
                      {preset.name}
                    </span>
                    <span className="text-[10px] text-emerald-600 font-mono">
                      {preset.settings.desired_retention ? `${Math.round(preset.settings.desired_retention * 100)}%` : ''}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-snug">
                    {preset.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Desired Retention Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Sliders className="w-4 h-4 text-emerald-600" />
              目标记忆保持率 (Desired Retention)
            </h3>
            <span className="text-sm font-mono font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
              {Math.round(settings.desired_retention * 100)}%
            </span>
          </div>

          <p className="text-xs text-slate-500 leading-relaxed">
            目标记忆率决定了卡片安排复习的时间点。根据遗忘曲线物理规律，若追求从 90% 提升至 95%，复习工作量将成倍激增。推荐设定在 <strong>85% ~ 90%</strong> 之间，以获得投入产出比最佳的学习平衡。
          </p>

          <div className="space-y-2 pt-2">
            <input
              type="range"
              min="0.70"
              max="0.98"
              step="0.01"
              value={settings.desired_retention}
              onChange={(e) =>
                setSettings({ ...settings, desired_retention: parseFloat(e.target.value) })
              }
              className="w-full accent-emerald-600 cursor-pointer"
            />
            <div className="flex justify-between text-[11px] text-slate-400 font-mono">
              <span>70% (轻松/粗记)</span>
              <span className="text-emerald-700 font-semibold">90% (推荐黄金平衡)</span>
              <span>98% (高强度/重度复习)</span>
            </div>
          </div>
        </div>

        {/* Daily limits & Learning Steps */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            日常限额与学习步骤
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                每日新卡上限 (New cards / day)
              </label>
              <input
                type="number"
                min="0"
                max="500"
                value={settings.new_cards_per_day}
                onChange={(e) =>
                  setSettings({ ...settings, new_cards_per_day: parseInt(e.target.value) || 0 })
                }
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">建议 15~30 个，积少成多</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                每日最大复习上限 (Reviews / day)
              </label>
              <input
                type="number"
                min="0"
                max="2000"
                value={settings.reviews_per_day}
                onChange={(e) =>
                  setSettings({ ...settings, reviews_per_day: parseInt(e.target.value) || 0 })
                }
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">建议 150~300 次，根据精力调整</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                新卡初学步骤 (分钟，以逗号分隔)
              </label>
              <input
                type="text"
                value={learningStepsStr}
                onChange={(e) => setLearningStepsStr(e.target.value)}
                placeholder="1, 10"
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">默认 1, 10 (即 1 分钟后首查，10 分钟后二查)</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                遗忘重学步骤 (分钟，以逗号分隔)
              </label>
              <input
                type="text"
                value={relearningStepsStr}
                onChange={(e) => setRelearningStepsStr(e.target.value)}
                placeholder="10"
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">打分 Again 时进入的短期重复步骤</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                最大排期间隔 (天)
              </label>
              <input
                type="number"
                min="30"
                max="36500"
                value={settings.maximum_interval_days}
                onChange={(e) =>
                  setSettings({ ...settings, maximum_interval_days: parseInt(e.target.value) || 36500 })
                }
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                难词绊脚石阈值 (Leech)
              </label>
              <input
                type="number"
                min="3"
                max="30"
                value={settings.leech_threshold}
                onChange={(e) =>
                  setSettings({ ...settings, leech_threshold: parseInt(e.target.value) || 8 })
                }
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                每日刷新时间点 (小时)
              </label>
              <input
                type="number"
                min="0"
                max="23"
                value={settings.day_start_hour}
                onChange={(e) =>
                  setSettings({ ...settings, day_start_hour: parseInt(e.target.value) || 4 })
                }
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">如 4 代表凌晨 4 点视作新的一天</span>
            </div>
          </div>

          {/* Toggle Switches */}
          <div className="space-y-3 pt-3 border-t border-slate-100">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_fuzzing}
                onChange={(e) => setSettings({ ...settings, enable_fuzzing: e.target.checked })}
                className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
              />
              <div>
                <span className="text-xs font-semibold text-slate-800">启用间隔随机微调 (Enable Fuzzing)</span>
                <p className="text-[11px] text-slate-500">
                  为复习排期加入极小随机抖动，避免多个卡片在同一天突然扎堆。
                </p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.bury_siblings}
                onChange={(e) => setSettings({ ...settings, bury_siblings: e.target.checked })}
                className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
              />
              <div>
                <span className="text-xs font-semibold text-slate-800">关联兄弟卡片避嫌 (Bury Siblings)</span>
                <p className="text-[11px] text-slate-500">
                  同一单词的英译中卡片和拼写卡片不在同一天出现，防止相互提示干扰测试真实性。
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* FSRS Weights (21 weights) */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-900 text-sm">
              FSRS 权重参数 (21个浮点数)
            </h3>
            <button
              type="button"
              onClick={() => setWeightsStr('')}
              className="text-xs text-blue-600 hover:underline"
            >
              清空恢复为 FSRS 官方默认权重
            </button>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            留空将采用开源 FSRS 官方默认全局优化权重。若后续通过你个人的复习记录优化出 21 参，可在此粘贴。
          </p>
          <textarea
            rows={2}
            value={weightsStr}
            onChange={(e) => setWeightsStr(e.target.value)}
            placeholder="留空即为默认，或填入如 0.40255, 1.18385, 3.173, ..."
            className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500 text-slate-700"
          />
        </div>

        {/* Save Settings Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg shadow-sm disabled:opacity-50 transition-colors cursor-pointer"
          >
            <Save className="w-4 h-4" />
            {saving ? '正在保存...' : '保存算法设置'}
          </button>
        </div>
      </form>

      {/* Full Backup Export Card */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Download className="w-4 h-4 text-slate-700" />
              全量数据备份导出
            </h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              导出包含所有词本、生词条目、学习卡片、复习历史流水及个人设置的完整 JSON 备份文件，数据所有权完全归你所有。
            </p>
          </div>
          <button
            type="button"
            disabled={exporting}
            onClick={handleExportBackup}
            className="flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg shadow-sm disabled:opacity-50 transition-colors cursor-pointer"
          >
            <Download className="w-3.5 h-3.5" />
            {exporting ? '正在生成...' : '立即下载 JSON 备份'}
          </button>
        </div>
      </div>

      {/* Educational Notice on FSRS vs SM-2 */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-xs text-slate-600 space-y-2">
        <div className="font-bold text-slate-800 text-sm flex items-center gap-2">
          <Info className="w-4 h-4 text-emerald-600" />
          认知科学说明：FSRS 评分与复习概念
        </div>
        <p className="leading-relaxed">
          • <strong>四档评分</strong>：Again (重来，完全遗忘) / Hard (困难，回忆吃力) / Normal (良好，对应 FSRS 官方算法的标准 Good 回忆) / Easy (简单，毫无迟疑秒答)。
        </p>
        <p className="leading-relaxed">
          • <strong>FSRS 与陈旧 SM-2 的本质区别</strong>：SM-2 仅使用单一的简陋乘数 (Ease Factor)，在遇到复习中断时极易发生“复习雪崩”；而 FSRS 严格将记忆抽象为<strong>稳定性 (S, Stability)</strong> 与<strong>难度 (D, Difficulty)</strong> 两个独立物理变量，根据负幂律曲线精确计算下一阶段的预测遗忘点。
        </p>
      </div>
    </div>
  );
};
